import React, { FC, useId, useState } from 'react';
import ReactMarkdown, { ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { Minus, Plus } from 'lucide-react';
import classNames from 'classnames';
import { getShortenCountryName } from '@/lib/use-origin-country';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { rehypeNormalizeHeadings, stripDuplicateTitleHeading } from './helpers';

const t = i18n.init('product');

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

const applySubstitutions = (
  text: string,
  variables: Record<string, string>,
): string => {
  let result = text
    .replace(/^(#{1,6})([^ #\n])/gm, '$1 $2') // ensure space after # for CommonMark compliance
    .replace(/\b12 months?\b/g, (match) => {
      if (['reebelo-us', 'quista'].includes(settings.store)) {
        return `up to ${match}`;
      }

      return match;
    })
    .replace(/\b(30|14) days\b/g, () => `${freeReturnDays} days`)
    .replace(
      /\[Reebelo\]\([^)]*\)/g,
      `[Reebelo](${
        typeof window !== 'undefined' ? window.location.origin : ''
      })`,
    );

  Object.entries(variables).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
  });

  return result;
};

const MAX_CHARS = 500;

// Collapsed height in lines, roughly what MAX_CHARS used to show.
const COLLAPSED_CLASS = 'line-clamp-[12] md:line-clamp-6';

/**
 * Renders the tag `rehypeNormalizeHeadings` settled on, styled by the level
 * the catalogue authored so the description looks as it always has.
 */
const DescriptionHeading = ({
  node,
  children,
  compact,
}: React.HTMLAttributes<HTMLHeadingElement> &
  ExtraProps & { compact: boolean }) => {
  const Tag = node?.tagName === 'h4' ? 'h4' : 'h3';
  const sourceLevel = Number(node?.properties?.dataSourceLevel);

  return (
    <Tag
      className={classNames({
        'mb-2 text-xl font-bold text-gray-700 xs:text-2xl': sourceLevel === 1,
        'pt-8': sourceLevel === 1 && node?.position?.start.line !== 1,
        'text-lg xs:text-xl': sourceLevel === 1 && compact,
        'mb-2 mt-8 text-sm font-semibold xs:text-base': sourceLevel === 2,
        'text-xs font-semibold xs:text-sm': sourceLevel === 3,
      })}
    >
      {children}
    </Tag>
  );
};

const ProductDescription: FC<{
  text: string;
  shouldShowDisclaimer: boolean;
  compact?: boolean;
  productName?: string;
}> = ({ text, shouldShowDisclaimer, compact = false, productName }) => {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  const variables = {
    country: getShortenCountryName(),
    free_return_days: `${freeReturnDays}`,
    warranty_month: ['reebelo-us', 'quista'].includes(settings.store)
      ? `up to ${settings.warranty_month}`
      : `${settings.warranty_month}`,
  };

  const processedText = applySubstitutions(
    stripDuplicateTitleHeading(text, productName),
    variables,
  );
  const isLong = processedText.length > MAX_CHARS;

  return (
    <div className="text-sm leading-normal">
      <div>
        {/* The full text is always in the HTML so crawlers see all of it;
            the collapsed state is CSS only. */}
        <div
          id={contentId}
          className={classNames({ [COLLAPSED_CLASS]: isLong && !expanded })}
          // A link below the clamp is still focusable, so expand rather than
          // let keyboard focus land on hidden text.
          onFocus={() => setExpanded(true)}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeNormalizeHeadings]}
            components={{
              h3: (props) => (
                <DescriptionHeading {...props} compact={compact} />
              ),
              h4: (props) => (
                <DescriptionHeading {...props} compact={compact} />
              ),
              a: ({ href, children }) => (
                <a
                  className="underline"
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {children}
                </a>
              ),
              p: ({ children }) => <div>{children}</div>,
            }}
          >
            {processedText}
          </ReactMarkdown>
        </div>
        {shouldShowDisclaimer && (
          <>
            <h3 className="mt-5 text-base font-bold">
              Warning: Read Before Purchase
            </h3>
            <p>
              Each state and territory in Australia has different rules and
              regulations pertaining to the use of e-scooters and other electric
              mobility devices (including e-bikes, hoverboards/e-skateboards,
              and go karts), including where you can legally use such products,
              and if you need to register them with your state's road traffic
              authority. It is the responsibility of each user to check and
              abide by their local road traffic laws before riding. Exercise
              caution and always wear a helmet and protective gear when riding.
            </p>
          </>
        )}
      </div>
      {isLong && (
        <button
          type="button"
          className="mt-5 flex items-center gap-1 text-sm font-semibold underline"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? t`Show Less` : t`Show More`}
          {expanded ? (
            <Minus className="h-3.5 w-3.5" strokeWidth={3} />
          ) : (
            <Plus className="h-3.5 w-3.5" strokeWidth={3} />
          )}
        </button>
      )}
    </div>
  );
};

export default ProductDescription;
